import React, { createContext, useContext, useState, useEffect, useMemo, useCallback } from 'react';
import { SUPPORTED_LANGUAGES, getStaticText, staticTranslations } from '../translations/staticTranslations';
import TranslationService from '../utils/translationService';
import DomTranslator from '../utils/domTranslator';

const LanguageContext = createContext(null);

export const LanguageProvider = ({ children }) => {
  // Read initial language from localStorage, defaulting to 'en'
  const [currentLang, setCurrentLang] = useState(() => {
    try {
      const saved = localStorage.getItem('gpn_admin_language');
      if (saved && SUPPORTED_LANGUAGES.some((l) => l.code === saved)) {
        return saved;
      }
    } catch (e) {}
    return 'en';
  });

  // Track module-specific language overrides
  const [moduleLanguages, setModuleLanguages] = useState(() => {
    const map = {};
    try {
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith('gpn_mod_lang_')) {
          const modKey = key.replace('gpn_mod_lang_', '');
          const val = localStorage.getItem(key);
          if (val && val !== 'default') {
            map[modKey] = val;
          }
        }
      }
    } catch (e) {}
    return map;
  });

  // Listen for module language changes from ModuleSettingsModal
  useEffect(() => {
    const handleModChange = (e) => {
      const { moduleKey, language } = e.detail || {};
      if (moduleKey) {
        setModuleLanguages((prev) => {
          const updated = { ...prev };
          if (!language || language === 'default') {
            delete updated[moduleKey];
          } else {
            updated[moduleKey] = language;
          }
          return updated;
        });
      }
    };

    window.addEventListener('gpn_module_language_changed', handleModChange);
    return () => window.removeEventListener('gpn_module_language_changed', handleModChange);
  }, []);

  // Helper to get effective language for a specific module
  const getModuleLanguage = useCallback((modKey) => {
    if (!modKey) return currentLang;
    const direct = moduleLanguages[modKey];
    if (direct && direct !== 'default') return direct;

    // Check alias mappings (e.g. 'prayers' vs 'prayer')
    const aliases = {
      prayers: 'prayer',
      prayer: 'prayers',
      special_prayers: 'special-prayer-categories',
      'special-prayer-categories': 'special_prayers',
    };
    const alias = aliases[modKey];
    if (alias && moduleLanguages[alias] && moduleLanguages[alias] !== 'default') {
      return moduleLanguages[alias];
    }

    return currentLang;
  }, [currentLang, moduleLanguages]);

  // Helper to set module language
  const setModuleLanguage = useCallback((modKey, langCode) => {
    if (!modKey) return;
    if (!langCode || langCode === 'default') {
      localStorage.removeItem(`gpn_mod_lang_${modKey}`);
    } else {
      localStorage.setItem(`gpn_mod_lang_${modKey}`, langCode);
    }
    window.dispatchEvent(new CustomEvent('gpn_module_language_changed', {
      detail: { moduleKey: modKey, language: langCode }
    }));
  }, []);

  // Current language metadata object
  const currentLanguageObj = useMemo(() => {
    return SUPPORTED_LANGUAGES.find((l) => l.code === currentLang) || SUPPORTED_LANGUAGES[0];
  }, [currentLang]);

  // Persist language on change and activate full-website DOM translator
  useEffect(() => {
    try {
      localStorage.setItem('gpn_admin_language', currentLang);
      document.documentElement.setAttribute('lang', currentLang);
      DomTranslator.setLanguage(currentLang);
    } catch (e) {}
  }, [currentLang]);

  // Handler to switch language globally
  const changeLanguage = useCallback((code) => {
    if (!code) return;
    const cleanCode = code.toLowerCase().trim();
    if (SUPPORTED_LANGUAGES.some((l) => l.code === cleanCode)) {
      try {
        localStorage.setItem('gpn_admin_language', cleanCode);
        document.documentElement.setAttribute('lang', cleanCode);
        DomTranslator.setLanguage(cleanCode);
        window.dispatchEvent(new CustomEvent('gpn_admin_language_changed', { detail: { lang: cleanCode } }));
      } catch (e) {}
      setCurrentLang(cleanCode);
    }
  }, []);

  // Static translation resolver helper
  const t = useCallback((key, fallback = '') => {
    return getStaticText(currentLang, key, fallback);
  }, [currentLang]);

  // Dynamic server-data translation helper
  const translateDynamic = useCallback((text) => {
    return TranslationService.translate(text, currentLang);
  }, [currentLang]);

  const value = useMemo(() => ({
    currentLang,
    currentLanguageObj,
    changeLanguage,
    t,
    translateDynamic,
    moduleLanguages,
    getModuleLanguage,
    setModuleLanguage,
    supportedLanguages: SUPPORTED_LANGUAGES,
    staticTranslations,
  }), [currentLang, currentLanguageObj, changeLanguage, t, translateDynamic, moduleLanguages, getModuleLanguage, setModuleLanguage]);

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = (moduleKey) => {
  const context = useContext(LanguageContext);
  if (!context) {
    // Graceful fallback if invoked outside provider
    return {
      currentLang: 'en',
      currentLanguageObj: SUPPORTED_LANGUAGES[0],
      changeLanguage: () => {},
      t: (key, fallback = '') => getStaticText('en', key, fallback),
      translateDynamic: (text) => Promise.resolve(text),
      supportedLanguages: SUPPORTED_LANGUAGES,
      moduleLanguages: {},
      getModuleLanguage: () => 'en',
      setModuleLanguage: () => {},
    };
  }

  // If a moduleKey is specified, scope currentLang and helpers to that module!
  if (moduleKey) {
    const modLang = context.getModuleLanguage(moduleKey);
    const modLangObj = SUPPORTED_LANGUAGES.find((l) => l.code === modLang) || SUPPORTED_LANGUAGES[0];
    return {
      ...context,
      currentLang: modLang,
      currentLanguageObj: modLangObj,
      t: (key, fallback = '') => getStaticText(modLang, key, fallback),
      translateDynamic: (text) => TranslationService.translate(text, modLang),
    };
  }

  return context;
};

export default LanguageContext;
